import { sendErrorToServices } from 'lib/logging';
import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

function useShortURL(url: string) {
  const [shortURL, setShortURL] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { refetch } = useQuery({
    queryKey: ['shortenURL', url],
    queryFn: async () => {
      setLoading(true);
      setError(null);

      if (typeof window === 'undefined') {
        setLoading(false);
        setError('window is not defined');
        return null;
      }

      const urlToShorten = url;

      try {
        const response = await fetch('/api/shorten-url', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ url: urlToShorten })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              `Failed to get the short URL. Status: ${response.status}`
          );
        }

        const data = await response.json();
        const shortUrl = data.shortUrl;

        if (!shortUrl) {
          throw new Error('No short URL in response');
        }

        setShortURL(shortUrl);
        return shortUrl;
      } catch (err) {
        const error = err as Error;
        setError(error.message);
        sendErrorToServices('Failed to get the short URL', error.message);

        // Return null instead of throwing to prevent React Query errors
        // that could affect UI components like tooltips
        return null;
      } finally {
        setLoading(false);
      }
    },
    enabled: false,
    retry: false, // Don't retry failed URL shortening attempts
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000 // Keep in cache for 10 minutes
  });

  return { shortURL, loading, error, refetch };
}

export { useShortURL };
